// @ts-nocheck
import { useState } from 'react'
import { useQuery } from 'react-query'
import { 
  Server, 
  Play, 
  Square, 
  RotateCcw, 
  RefreshCw,
  FileText,
  CheckCircle,
  AlertCircle,
} from 'lucide-react'
import { apiService } from '../services/api'
import { authService } from '../services/auth'

const BindManager = () => {
  const isAdmin = authService.isAdmin()
  const user = authService.getUser()
  const isSuperAdmin = user?.role === 'superadmin'
  const [activeTab, setActiveTab] = useState('status')

  const { data: status, refetch: refetchStatus } = useQuery('bindStatus',
    () => apiService.getDNSServerStatus(),
    { refetchInterval: 30000 }
  )

  const { data: config } = useQuery('bindConfig', () => apiService.getDNSServerConfig())
  const { data: logs, refetch: refetchLogs } = useQuery('dnsServiceLogs', () => apiService.getDNSServerLogs(), {
    enabled: activeTab === 'logs',
    refetchInterval: activeTab === 'logs' ? 30000 : false,
  })

  const configRows = (() => {
    const content = config?.data?.content || ''
    const rows: any[] = []
    const optionBlock = content.match(/options\s*\{([\s\S]*?)\n\};/)
    if (optionBlock && isSuperAdmin) {
      optionBlock[1].split('\n').forEach((line: string) => {
        const clean = line.trim().replace(/;$/, '')
        if (!clean || clean.startsWith('//')) return
        const match = clean.match(/^([a-zA-Z0-9_-]+)\s+(.+)$/)
        if (match) rows.push({ section: 'Options', key: match[1], value: match[2] })
      })
    }
    const zones = [...content.matchAll(/zone\s+"([^"]+)"(?:\s+IN)?\s*\{([\s\S]*?)\};/g)]
    zones.forEach((zone: any) => {
      const block = zone[2]
      const type = block.match(/type\s+([^;]+);/)?.[1]?.trim() || 'N/A'
      rows.push({ section: 'Zone', key: zone[1], value: type })
    })
    return rows
  })()

  const handleServiceAction = async (action: string) => {
    try {
      switch (action) {
        case 'start':
          await apiService.startDNSServer()
          break
        case 'stop':
          await apiService.stopDNSServer()
          break
        case 'restart':
          await apiService.restartDNSServer()
          break
        case 'reload':
          await apiService.reloadDNSServer()
          break
      }
      refetchStatus()
    } catch (error) {
      alert(`Failed to ${action} DNS service`)
    }
  }

  const handleValidateConfig = async () => {
    try {
      await apiService.validateBindConfig()
      alert('Configuration is valid')
    } catch (error) {
      alert('Configuration validation failed')
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">DNS Server Manager</h2>
          <p className="text-slate-500">Control and configure the DNS service</p>
        </div>
        <button
          onClick={() => refetchStatus()}
          className="flex items-center space-x-2 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
        >
          <RefreshCw className="h-4 w-4" />
          <span>Refresh</span>
        </button>
      </div>

      {/* Service Status Card */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex items-center space-x-4">
            <div className={`h-16 w-16 rounded-full flex items-center justify-center ${
              status?.data?.running ? 'bg-green-100' : 'bg-red-100'
            }`}>
              <Server className={`h-8 w-8 ${
                status?.data?.running ? 'text-green-600' : 'text-red-600'
              }`} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900">
                DNS Service {status?.data?.running ? 'Running' : 'Stopped'}
              </h3>
              <p className="text-slate-500">
                Version: {status?.data?.version || 'Unknown'} | 
                PID: {status?.data?.pid || 'N/A'} | 
                Uptime: {status?.data?.uptime || 'N/A'}
              </p>
            </div>
          </div>
          
          {isAdmin && (
            <div className="grid grid-cols-3 gap-2 sm:flex sm:items-center sm:space-x-3">
              {status?.data?.running ? (
                <>
                  <button
                    onClick={() => handleServiceAction('reload')}
                    className="flex items-center space-x-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
                  >
                    <RefreshCw className="h-4 w-4" />
                    <span>Reload</span>
                  </button>
                  <button
                    onClick={() => handleServiceAction('restart')}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <RotateCcw className="h-4 w-4" />
                    <span>Restart</span>
                  </button>
                  <button
                    onClick={() => handleServiceAction('stop')}
                    className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <Square className="h-4 w-4" />
                    <span>Stop</span>
                  </button>
                </>
              ) : (
                <button
                  onClick={() => handleServiceAction('start')}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <Play className="h-4 w-4" />
                  <span>Start</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200">
        <nav className="flex space-x-8">
          {['status', 'config', 'logs'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-4 px-1 border-b-2 font-medium text-sm capitalize transition-colors ${
                activeTab === tab
                  ? 'border-sky-600 text-sky-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              {tab}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200">
        {activeTab === 'status' && (
          <div className="p-6 space-y-6">
            <h3 className="text-lg font-semibold text-slate-900">Service Details</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">Service Status</p>
                <div className="flex items-center space-x-2 mt-1">
                  <span className={`h-2 w-2 rounded-full ${
                    status?.data?.running ? 'bg-green-500' : 'bg-red-500'
                  }`} />
                  <span className="font-medium text-slate-900">
                    {status?.data?.running ? 'Running' : 'Stopped'}
                  </span>
                </div>
              </div>

              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">Configuration Valid</p>
                <div className="flex items-center space-x-2 mt-1">
                  {status?.data?.config_valid ? (
                    <>
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="font-medium text-green-600">Valid</span>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="h-4 w-4 text-red-500" />
                      <span className="font-medium text-red-600">Invalid</span>
                    </>
                  )}
                </div>
              </div>

              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">Version</p>
                <p className="font-medium text-slate-900">{status?.data?.version || 'Unknown'}</p>
              </div>

              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">Process ID</p>
                <p className="font-medium text-slate-900">{status?.data?.pid || 'N/A'}</p>
              </div>

              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">Uptime</p>
                <p className="font-medium text-slate-900">{status?.data?.uptime || 'N/A'}</p>
              </div>

              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">Enabled on Boot</p>
                <p className="font-medium text-slate-900">
                  {status?.data?.enabled ? 'Yes' : 'No'}
                </p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'config' && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">DNS Configuration Summary</h3>
                <p className="text-sm text-slate-500">Structured view of active DNS service configuration</p>
              </div>
              {isAdmin && (
                <div className="flex items-center space-x-3">
                  <button onClick={handleValidateConfig} className="flex items-center space-x-2 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors">
                    <CheckCircle className="h-4 w-4" />
                    <span>Validate</span>
                  </button>
                </div>
              )}
            </div>

            <div className="border rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-500">Section</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-500">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-500">Value</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                  {configRows.map((row, idx) => (
                    <tr key={`${row.section}-${row.key}-${idx}`} className="hover:bg-slate-50">
                      <td className="px-4 py-3 text-sm text-slate-500">{row.section}</td>
                      <td className="px-4 py-3 text-sm font-medium text-slate-900">{row.key}</td>
                      <td className="px-4 py-3 text-sm text-slate-700 break-all">{row.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'logs' && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">Service Logs</h3>
              <button onClick={() => refetchLogs()} className="px-3 py-2 border rounded-lg text-sm hover:bg-slate-50">Refresh</button>
            </div>
            <div className="bg-slate-900 rounded-lg p-4 font-mono text-sm text-slate-300 h-96 overflow-y-auto">
              {(logs?.data?.lines || []).length === 0 ? (
                <p>No recent service log entries found</p>
              ) : logs.data.lines.map((line: string, index: number) => (
                <p key={index} className="whitespace-pre-wrap break-words">{line}</p>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default BindManager
